import React, { memo } from 'react';
import { Shield, Swords, Users, MapPin, Zap, User, ShieldUser } from "lucide-react";


const Hexagon = memo(({
    row, col, terrain, countryId, nouveau, hasResource, resources, isCity, status, lose, biome, ocean, name,
    getHexPosition, getHexPoints, getCountryColor, countryCenters, countryList, countries_dict, onOpenPopin,
    animationPhase = null
}) => {
    const [x, y] = getHexPosition(row, col);
    const points = getHexPoints(x, y);
    const fill = countryId ? getCountryColor(name) : terrain === 'water' ? '#00000000' : '#a3e635';
    const class_new = nouveau ? "cell_new" : "";

    // Conference animation classes (only active when animationPhase is set)
    const isCoastalSubmerged = animationPhase !== null && animationPhase >= 1 && biome === "cotier";
    const isWarZoneAnimated = animationPhase !== null && animationPhase >= 2 && nouveau;
    const conferenceAnimClass = isCoastalSubmerged
        ? "conference-submerge"
        : isWarZoneAnimated
            ? "conference-war-reveal"
            : "";
    const hasResourceAnimation = (animationPhase === 0 && hasResource) || (animationPhase >= 1 && hasResource && biome !== "cotier");
    const hasNouveauAnimation = animationPhase >= 2 && nouveau;
    const countryShortName = countryList.find(n => countries_dict[n].id === countryId);
    const data = countries_dict[countryShortName] || {};

    const isCenter = countryId &&
        countryCenters[countryId]?.x === col &&
        countryCenters[countryId]?.y === row;
    const patternId = `hachurePattern-${countryId}`;
    const warPatternId = `warPattern-${countryId}`;

    const handleOpenPopin = () => {
        if (onOpenPopin) {
            onOpenPopin({
                countryShortName
            });
        }
    };

    return (terrain === 'water' ? (
        <g key={`${row}-${col}`}></g>
    ) : (
        <g key={`${row}-${col}`} onClick={handleOpenPopin} style={{ cursor: 'pointer' }}>
            <defs>
                <pattern id={patternId} patternUnits="userSpaceOnUse" width="6" height="6">
                    <rect width="6" height="6" fill={getCountryColor(name)} />
                    <path d="M0,6 L6,0" stroke="#ff0000" strokeWidth="1" />
                </pattern>
                {isWarZoneAnimated && (
                    <pattern id={warPatternId} patternUnits="userSpaceOnUse" width="6" height="6">
                        <rect width="6" height="6" fill="#1e40af" />
                        <path d="M0,6 L6,0" stroke="#ff0000" strokeWidth="1" />
                    </pattern>
                )}
            </defs>

            <polygon
                points={points}
                fill={isWarZoneAnimated ? `url(#${warPatternId})` : (nouveau && animationPhase === null) ? `url(#${patternId})` : fill}
                stroke={'#0f172a'}
                strokeWidth={1}
                className={`${class_new} ${conferenceAnimClass}`}
            />
            {/* Indicateur de ressources */}
            {(hasResource && animationPhase === null) || (animationPhase != null && hasResourceAnimation) && (
                <g>
                    <circle
                        cx={x}
                        cy={y}
                        r={Math.max(6, 12 * resources / 100)}
                        fill="rgba(59, 130, 246, 0.8)"
                        stroke="#1e40af"
                        strokeWidth="2"
                        className="animate-pulse"
                    />
                    <circle
                        cx={x}
                        cy={y}
                        r={Math.max(3, 6 * resources / 100)}
                        fill="#60a5fa"
                    />
                </g>
            )}

            {/* Icône biome cotier */}
            {biome == "cotier" && ocean > 80 && (
                <g>
                    <circle cx={x} cy={y} r="12" fill="#000000" stroke="#ffffff" strokeWidth="2" />
                    <text x={x} y={y + 4} fontSize="14" textAnchor="middle" fill="#60a5fa">🏖️</text>
                </g>
            )}
            {/* Indicateur de défaite */}
            {lose && (
                <g>
                    <circle cx={x} cy={y} r="10" fill="#ef4444" stroke="#dc2626" strokeWidth="2" />
                    <text x={x} y={y + 4} fontSize="12" textAnchor="middle" fill="white">✕</text>
                </g>
            )}
            {/* Label du pays au centre */}
            {data && isCenter && (
                <foreignObject x={x - 60} y={y - 70} width="120" height="70" style={{ pointerEvents: 'auto' }}>
                    <div className="flex items-center justify-center h-full" onClick={handleOpenPopin} style={{ pointerEvents: 'auto' }}>
                        <div className={`
                            px-3 py-1.5 rounded-lg backdrop-blur-xl border-2 transition-all duration-200
                            ${status === 'allies' ? 'bg-emerald-500/20 border-emerald-400 text-emerald-100' :
                                status === 'ennemi' ? 'bg-red-500/20 border-red-400 text-red-100' :
                                    status === 'joueur' ? 'bg-blue-500/20 border-blue-400 text-blue-100' :
                                        'bg-slate-700/30 border-slate-400 text-slate-100'
                            }
                            hover:scale-105 cursor-pointer shadow-lg
                        `}>
                            <div className="flex items-center gap-1 font-medium" style={{ fontSize: '12px' }}>
                                {status === 'allies' && <Shield size={12} className="text-emerald-400" />}
                                {status === 'ennemi' && <Swords size={12} className="text-red-400" />}
                                {status === 'neutre' && <MapPin size={12} className="text-slate-400" />}
                                {status === 'joueur' && <User size={12} className="text-blue-400" />}
                                <span className={`truncate max-w-[80px] ${lose ? 'line-through text-black' : ''}`}>{countryShortName}</span>
                            </div>
                        </div>
                    </div>
                </foreignObject>
            )}
            {/* Icône de ville - rendu après le label pour être par-dessus */}
            {isCity && !isCoastalSubmerged && (
                <g>
                    <circle cx={x} cy={y} r="12" fill="#000000" stroke="#ffffff" strokeWidth="2" />
                    <text x={x} y={y + 5} fontSize="12" textAnchor="middle" fill="#ffffff">🏢</text>
                </g>
            )}
        </g>
    ));
});

export default Hexagon;